/**
 * Local runtime: log destinations and observability exports.
 *
 * @module
 */
import { resolveAndPersistPreferredEditor } from "../../editorPreferences";
import { isElectron } from "../../env";
import {
  ForgeRuntimeViewOverride,
  readRuntimeSurfaceState,
  readRuntimeViewOverride,
  writeRuntimeViewOverride,
} from "../../environments/runtimeSurface";
import { useUpdateSettings } from "../../hooks/useSettings";
import { ensureLocalApi } from "../../localApi";
import { useServerAvailableEditors, useServerObservability } from "../../rpc/serverState";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { RUNTIME_VIEW_OVERRIDE_LABELS } from "./RuntimeDiagnosticsDataView";
import { SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { MonitorCogIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

function ObservabilitySection() {
  const observability = useServerObservability();
  const { updateSettings } = useUpdateSettings();
  const observedLogsUrl = observability?.otlpLogsUrl ?? "";
  const observedTracesUrl = observability?.otlpTracesUrl ?? "";
  const observedMetricsUrl = observability?.otlpMetricsUrl ?? "";
  const [logsUrl, setLogsUrl] = useState(observedLogsUrl);
  const [tracesUrl, setTracesUrl] = useState(observedTracesUrl);
  const [metricsUrl, setMetricsUrl] = useState(observedMetricsUrl);

  useEffect(() => {
    setTracesUrl(observedTracesUrl);
    setMetricsUrl(observedMetricsUrl);
    setLogsUrl(observedLogsUrl);
  }, [observedLogsUrl, observedMetricsUrl, observedTracesUrl]);

  const saveOtlpField = useCallback(
    (field: "otlpLogsUrl" | "otlpTracesUrl" | "otlpMetricsUrl", value: string) => {
      updateSettings({ observability: { [field]: value } as any });
    },
    [updateSettings],
  );

  return (
    <SettingsSection title="Observability">
      <SettingsRow
        title="OTLP Traces URL"
        description="Send trace spans to an OTLP-compatible endpoint (e.g. Grafana Tempo, Jaeger)."
        control={
          <Input
            className="h-7 w-72 font-mono text-xs"
            placeholder="https://otel.example.com/v1/traces"
            value={tracesUrl}
            onChange={(e) => setTracesUrl(e.target.value)}
            onBlur={() => saveOtlpField("otlpTracesUrl", tracesUrl)}
          />
        }
      />
      <SettingsRow
        title="OTLP Metrics URL"
        description="Send metrics to an OTLP-compatible endpoint."
        control={
          <Input
            className="h-7 w-72 font-mono text-xs"
            placeholder="https://otel.example.com/v1/metrics"
            value={metricsUrl}
            onChange={(e) => setMetricsUrl(e.target.value)}
            onBlur={() => saveOtlpField("otlpMetricsUrl", metricsUrl)}
          />
        }
      />
      <SettingsRow
        title="OTLP Logs URL"
        description="Send structured logs to an OTLP-compatible endpoint (e.g. Loki, Grafana Cloud)."
        control={
          <Input
            className="h-7 w-72 font-mono text-xs"
            placeholder="https://otel.example.com/v1/logs"
            value={logsUrl}
            onChange={(e) => setLogsUrl(e.target.value)}
            onBlur={() => saveOtlpField("otlpLogsUrl", logsUrl)}
          />
        }
      />
    </SettingsSection>
  );
}

function RuntimeDiagnosticsSection() {
  const [override, setOverride] = useState<ForgeRuntimeViewOverride>(() =>
    readRuntimeViewOverride(),
  );
  const runtimeSurface = readRuntimeSurfaceState();
  const showRuntimeDiagnostics =
    isElectron && (import.meta.env.DEV || runtimeSurface.isDevOverrideActive);
  if (!showRuntimeDiagnostics) return null;

  const setRuntimeOverride = (value: ForgeRuntimeViewOverride) => {
    setOverride(value);
    writeRuntimeViewOverride(value);
    window.setTimeout(() => window.location.reload(), 0);
  };

  return (
    <SettingsSection title="Runtime state">
      <SettingsRow
        title={
          <span className="inline-flex items-center gap-2">
            <MonitorCogIcon className="size-4 text-muted-foreground/70" />
            Runtime view
          </span>
        }
        description="Internal development control for comparing Cloud, Desktop Local, and local web projections."
        status={
          <span className="block text-xs text-muted-foreground">
            Detected {runtimeSurface.surface}; execution {runtimeSurface.executionRuntime}; model{" "}
            {runtimeSurface.modelRuntime}
          </span>
        }
        control={
          <Select
            value={override}
            onValueChange={(value) => {
              if (value) setRuntimeOverride(value as ForgeRuntimeViewOverride);
            }}
          >
            <SelectTrigger className="w-full sm:w-64" aria-label="Runtime view override">
              <SelectValue>{RUNTIME_VIEW_OVERRIDE_LABELS[override]}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="end" alignItemWithTrigger={false}>
              {Object.entries(RUNTIME_VIEW_OVERRIDE_LABELS).map(([value, label]) => (
                <SelectItem key={value} hideIndicator value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        }
      />
      <SettingsRow
        title="Runtime execution boundary"
        description="Forge can use multiple runtimes. Projects and threads keep their own execution route."
        status={
          <span className="block text-xs text-muted-foreground">
            {runtimeSurface.executionRuntime === "desktop-local"
              ? "Desktop Local execution"
              : runtimeSurface.executionRuntime === "cloud"
                ? "Forge Cloud execution"
                : "Development local web execution"}
          </span>
        }
      />
    </SettingsSection>
  );
}

// moved verbatim from the 2909-line ProviderSettings.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function SystemSection() {
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const availableEditors = useServerAvailableEditors();
  const observability = useServerObservability();
  const logsDirectoryPath = observability?.logsDirectoryPath ?? null;
  // moved verbatim from the 2909-line ProviderSettings.tsx; complexity is
  // unchanged, and fallow reads a relocated function as a new one
  // fallow-ignore-next-line complexity
  const diagnosticsDescription = (() => {
    const exports: string[] = [];
    if (observability?.otlpTracesEnabled && observability.otlpTracesUrl) {
      exports.push(`traces to ${observability.otlpTracesUrl}`);
    }
    if (observability?.otlpMetricsEnabled && observability.otlpMetricsUrl) {
      exports.push(`metrics to ${observability.otlpMetricsUrl}`);
    }
    const mode = observability?.localTracingEnabled ? "Local trace file" : "Terminal logs only";
    return exports.length > 0 ? `${mode}. OTLP exporting ${exports.join(" and ")}.` : `${mode}.`;
  })();
  const openLogsDirectory = useCallback(() => {
    if (!logsDirectoryPath) return;
    setOpenError(null);
    setOpening(true);
    const editor = resolveAndPersistPreferredEditor(availableEditors ?? []);
    if (!editor) {
      setOpenError("No available editors found.");
      setOpening(false);
      return;
    }
    void ensureLocalApi()
      .shell.openInEditor(logsDirectoryPath, editor)
      .catch((error) => {
        setOpenError(error instanceof Error ? error.message : "Unable to open logs folder.");
      })
      .finally(() => setOpening(false));
  }, [logsDirectoryPath, availableEditors]);

  return (
    <>
      {isElectron && <ObservabilitySection />}
      <RuntimeDiagnosticsSection />
      {/* The `#system` anchor lives here, on the section that always renders.
          It was on ObservabilitySection, which is Electron-only, so
          /settings/system redirected to a hash the web build never mounts. */}
      <SettingsSection id="system" title="Logs">
        <SettingsRow
          {...searchableSetting("general-log-directory")}
          description={diagnosticsDescription}
          status={
            isElectron ? (
              <>
                <span className="block break-all font-mono text-[11px] text-foreground">
                  {logsDirectoryPath ?? "Resolving logs directory..."}
                </span>
                {openError ? (
                  <span className="mt-1 block text-destructive">{openError}</span>
                ) : null}
              </>
            ) : null
          }
          control={
            isElectron ? (
              <Button
                size="xs"
                variant="outline"
                disabled={!logsDirectoryPath || opening}
                onClick={openLogsDirectory}
              >
                {opening ? "Opening..." : "Open logs folder"}
              </Button>
            ) : null
          }
        />
      </SettingsSection>
    </>
  );
}
